/**
 * Mini Cart Drawer Component
 * Displays a popup cart drawer when items are added to cart or cart icon is clicked
 */

class MiniCartDrawer {
  constructor() {
    this.overlay = document.getElementById('mini-cart-drawer');
    this.itemsContainer = document.getElementById('mini-cart-items-container');
    this.subtotalElement = document.getElementById('mini-cart-subtotal');
    this.closeButton = this.overlay?.querySelector('.mini-cart-close');
    
    this.init();
  }

  init() {
    if (!this.overlay) return;

    // Close drawer when clicking overlay background
    this.overlay.addEventListener('click', (e) => {
      // Check if click is directly on the overlay (not on drawer content)
      if (e.target.id === 'mini-cart-drawer' || e.target.classList.contains('mini-cart-drawer-overlay')) {
        this.close();
      }
    });

    // Prevent clicks inside the drawer from closing it
    const drawerContent = this.overlay.querySelector('.mini-cart-drawer');
    if (drawerContent) {
      drawerContent.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Close drawer when clicking close button
    this.closeButton?.addEventListener('click', () => {
      this.close();
    });

    // Listen for cart icon clicks
    this.attachCartIconListeners();

    // Listen for add to cart events
    this.listenForCartUpdates();

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('active')) {
        this.close();
      }
    });
  }

  attachCartIconListeners() {
    const cartIcons = document.querySelectorAll('[data-open-mini-cart]');

    cartIcons.forEach(icon => {
      icon.addEventListener('click', (e) => {
        const me = /** @type {MouseEvent} */ (e);
        if (me.button !== 0 || me.metaKey || me.ctrlKey || me.shiftKey || me.altKey) return;
        e.preventDefault();
        this.open(icon);
      });
    });
  }

  listenForCartUpdates() {
    document.addEventListener('cart:update', (e) => {
      const data = (/** @type {any} */ (e).detail ?? {}).data;
      if (!data?.didError) {
        if (this.itemsContainer) this.itemsContainer.innerHTML = '<div class="mini-cart-loading"><span></span><span></span><span></span></div>';
        this.open();
      }
    });
  }

  async fetchAndRender() {
    try {
      const root = (Theme?.routes?.root_url ?? '/').replace(/\/+$/, '');
      const response = await fetch(`${root}/?sections=mini-cart-items`);
      const data = await response.json();
      const tmp = document.createElement('div');
      tmp.innerHTML = data['mini-cart-items'] ?? '';
      const rendered = tmp.querySelector('.mini-cart-items-rendered');
      if (!rendered || !this.itemsContainer) return;
      const subtotal = rendered.dataset.subtotal;
      if (subtotal && this.subtotalElement) this.subtotalElement.textContent = subtotal;
      this.itemsContainer.innerHTML = rendered.innerHTML;
      this.attachRemoveListeners();
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  attachRemoveListeners() {
    const removeButtons = this.itemsContainer.querySelectorAll('.mini-cart-item-remove');
    
    removeButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        const lineKey = e.target.dataset.lineKey;
        await this.removeItem(lineKey);
      });
    });
  }

  async removeItem(lineKey) {
    try {
      const changeUrl = (Theme?.routes?.cart_change_url ?? '/cart/change') + '.js';
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      if (response.ok) {
        await this.fetchAndRender();
        
        // Update cart count if it exists
        this.updateCartCount();
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  async updateCartCount() {
    try {
      const cartUrl = (Theme?.routes?.cart_url ?? '/cart') + '.js';
      const response = await fetch(cartUrl);
      const cart = await response.json();

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  open(/** @type {Element | undefined} */ triggerEl = undefined) {
    if (!this.overlay) return;

    const drawer = /** @type {HTMLElement|null} */ (this.overlay.querySelector('.mini-cart-drawer'));
    if (!drawer) return;

    const allAnchors = Array.from(document.querySelectorAll('[data-open-mini-cart]'));
    const anchor = triggerEl instanceof Element
      ? triggerEl
      : allAnchors.find(el => {
          const r = el.getBoundingClientRect();
          return r.width > 0 && r.height > 0 && r.top >= 0 && r.bottom <= window.innerHeight;
        }) || allAnchors.find(el => {
          const r = el.getBoundingClientRect();
          return r.width > 0 && r.height > 0;
        });

    const vw = window.innerWidth;
    const configuredWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--mini-cart-width')) || 380;
    const drawerWidth = Math.min(configuredWidth, vw - 16);

    drawer.style.position = 'fixed';
    drawer.style.width = drawerWidth + 'px';
    drawer.style.left = 'auto';

    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      const iconInView = rect.bottom > 0 && rect.bottom < window.innerHeight;
      const topPos = iconInView ? rect.bottom + 10 : 10;
      const rightPos = Math.max(8, Math.min(vw - rect.right, vw - drawerWidth - 8));
      const drawerLeftEdge = vw - rightPos - drawerWidth;
      const iconCenterX = rect.left + rect.width / 2;
      const arrowFromLeft = Math.max(8, Math.min(drawerWidth - 28, iconCenterX - drawerLeftEdge - 10));

      drawer.style.top = topPos + 'px';
      drawer.style.right = rightPos + 'px';
      drawer.style.setProperty('--mini-cart-arrow-right', (drawerWidth - arrowFromLeft - 20) + 'px');
    } else {
      drawer.style.top = '10px';
      drawer.style.right = '8px';
      drawer.style.setProperty('--mini-cart-arrow-right', '20px');
    }

    this.overlay.style.display = 'block';
    this.overlay.offsetHeight;
    this.overlay.classList.add('active');

    this.fetchAndRender();
  }

  close() {
    if (!this.overlay) return;
    
    this.overlay.classList.remove('active');
    // Don't restore body scroll since we never locked it
    
    setTimeout(() => {
      this.overlay.style.display = 'none';
    }, 300);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.miniCartDrawer = new MiniCartDrawer();
  });
} else {
  window.miniCartDrawer = new MiniCartDrawer();
}
